<template>
	<v-main>
		<v-container fluid>
			<v-card width="100%" outlined elevation-0>
				<v-toolbar flat class="border_bottom" shaped>
					<v-toolbar-title>Competitor</v-toolbar-title>
					<v-spacer></v-spacer>
					<v-btn color="primary" depressed class="mr-2" :to="{ name: 'competitor-analysis' }">
						<v-icon left>mdi-chart-box-outline</v-icon>
						Analisi
					</v-btn>
					<v-btn text title="Aggiorna" @click="aggiornaTutto"><v-icon>mdi-refresh</v-icon></v-btn>
				</v-toolbar>

				<v-card-text>
					<v-alert type="info" dense text dismissible>
						Inserisci fino a {{ max }} siti concorrenti: il sistema legge i loro prodotti, li abbina al tuo catalogo (EAN, SKU, MPN o AI sullo stesso brand) e in <strong>Analisi</strong> confronta i
						prezzi.
					</v-alert>

					<v-row>
						<v-col cols="12" md="5" lg="4">
							<site-list
								:sites="siti"
								:max="max"
								:selected-id="selezionato"
								:jobs="jobPerSito"
								:openai-active="openaiAttivo"
								@select="seleziona"
								@add="apriSito(null)"
								@edit="apriSito($event)"
								@rules="apriSito($event, 1)"
								@generate="avvia($event, 'generate_rules')"
								@delete="elimina"
								@scan="avvia($event, 'scan')"
								@match="avvia($event, 'match')"
								@cancel="annulla"
							></site-list>
						</v-col>
						<v-col cols="12" md="7" lg="8">
							<site-products-table
								v-if="sito"
								ref="prodotti"
								:site-id="sito._id"
								:site-name="sito.name"
								:refresh-key="refreshKey"
								@match="apriMatch"
								@error="mostra($event, 'error')"
							></site-products-table>
							<v-card v-else outlined class="pa-8 text-center grey--text">
								{{ siti.length ? 'Scegli un sito a sinistra per vederne i prodotti.' : 'Aggiungi un sito concorrente per iniziare.' }}
							</v-card>
						</v-col>
					</v-row>
				</v-card-text>
			</v-card>
		</v-container>


		<v-snackbar v-model="snack.visibile" :color="snack.colore" timeout="5000">{{ snack.testo }}</v-snackbar>
	</v-main>
</template>

<script>
import _ from 'lodash';
import SiteList from '../../components/competitor/SiteList.vue';
import SiteProductsTable from '../../components/competitor/SiteProductsTable.vue';
import { rememberListRoute } from '../../components/core/list/listReturn';
import { attivo, testoErrore, testoMessaggio, TIPI_JOB, TIPI_JOB_INTERNI, STATI_JOB, MAX_SITI } from '../../components/competitor/testi';

const POLLING_MS = 5000;

export default {
	name: 'CompetitorIndex',
	components: { SiteList, SiteProductsTable },
	data() {
		return {
			siti: [],
			max: MAX_SITI,
			selezionato: null,
			// job seguiti in tempo reale (per id): scansioni e abbinamenti
			jobs: {},
			timer: null,
			refreshKey: 0,
			// null = non noto: il pulsante AI resta attivo e il backend risponde openai_not_configured
			openaiAttivo: null,
			snack: { visibile: false, testo: '', colore: 'success' },
		};
	},
	computed: {
		sito() {
			return this.siti.find(s => s._id === this.selezionato) || null;
		},
		// job piu' recente per sito, fra quelli seguiti e l'ultimo restituito dal backend
		// (le copie di pagina per la taratura le segue solo il dialog)
		jobPerSito() {
			const out = {};
			this.siti.forEach(s => {
				const seguiti = Object.values(this.jobs).filter(j => j.competitor === s._id);
				const candidati = (s.lastJob ? [...seguiti, s.lastJob] : seguiti).filter(j => !TIPI_JOB_INTERNI.includes(j.type));
				const ultimo = _.maxBy(candidati, j => new Date(j.created_at).getTime());
				if (ultimo) out[s._id] = ultimo;
			});
			return out;
		},
	},
	sockets: {
		// il backend lo invia quando un job competitor arriva in fondo (watchdog brandimport)
		MESSAGE_COMPETITOR(raw) {
			let data;
			try {
				data = typeof raw === 'string' ? JSON.parse(raw) : raw;
			} catch (e) {
				return;
			}
			if (data && data.jobId) this.aggiornaJob(data.jobId);
		},
	},
	async created() {
		const q = this.$route.query.site;
		if (q) this.selezionato = String(q);
		await Promise.all([this.caricaSiti(), this.verificaOpenai()]);
		this.timer = setInterval(this.polling, POLLING_MS);
	},
	beforeDestroy() {
		clearInterval(this.timer);
	},
	methods: {
		mostra(testo, colore = 'success') {
			this.snack = { visibile: true, testo, colore };
		},
		async verificaOpenai() {
			try {
				const res = await this.$store.dispatch('services/getServiceFetchActive', ['openai']);
				const openai = _.find(res, s => s.service === 'openai');
				this.openaiAttivo = !!(openai && openai.active);
			} catch (e) {
				this.openaiAttivo = null;
			}
		},
		normalizza(job) {
			return { ...job, competitor: job.competitor ? String(job.competitor) : job.competitor };
		},
		async caricaSiti() {
			const res = await this.$store.dispatch('competitor/fetchSites');
			if (res.error) {
				this.mostra(testoErrore(res.error), 'error');
				return;
			}
			this.siti = (res.sites || []).map(s => (s.lastJob ? { ...s, lastJob: this.normalizza(s.lastJob) } : s));
			if (res.max) this.max = res.max;
			if (!this.sito && this.siti.length) this.seleziona(this.siti[0]._id);
			// riprende a seguire i job ancora in corso (es. dopo un refresh della pagina)
			this.siti.forEach(s => {
				if (attivo(s.lastJob) && !TIPI_JOB_INTERNI.includes(s.lastJob.type)) this.$set(this.jobs, s.lastJob._id, { ...s.lastJob, competitor: s._id });
			});
		},
		aggiornaTutto() {
			this.refreshKey++;
			this.caricaSiti();
		},
		seleziona(id) {
			this.selezionato = id;
			if (this.$route.query.site !== id) this.$router.replace({ query: { site: id } }).catch(() => {});
			// tornando da una pagina (sito, abbinamento) si riparte da questo sito
			rememberListRoute(this.$route.path, { site: id });
		},
		segui(job) {
			if (job && job._id) this.$set(this.jobs, job._id, this.normalizza(job));
		},
		// sito nuovo o da modificare: pagina (tab 1 = Regole); Indietro e Salva tornano qui
		apriSito(sito, tab = 0) {
			if (!sito) {
				this.$router.push('/competitor/sites/new');
				return;
			}
			this.$router.push({ path: `/competitor/sites/${sito._id}`, query: tab === 1 ? { tab: 'regole' } : {} });
		},
		async elimina(id) {
			const res = await this.$store.dispatch('competitor/deleteSite', id);
			if (res.error) {
				this.mostra(testoErrore(res.error), 'error');
				return;
			}
			if (this.selezionato === id) this.selezionato = null;
			this.mostra('Sito eliminato.');
			await this.caricaSiti();
		},
		async avvia(siteId, type) {
			const res = await this.$store.dispatch('competitor/createJob', { siteId, type });
			if (res.error) {
				// 409: c'e' gia' un job attivo, lo seguiamo comunque
				if (res.error.job) this.segui(res.error.job);
				this.mostra(testoErrore(res.error), 'error');
				return;
			}
			this.segui(res.job);
			const avvisi = {
				scan: 'Scansione avviata: le pagine vengono lette lentamente, puoi chiudere questa pagina.',
				match: 'Abbinamento avviato.',
				generate_rules: 'Generazione delle regole avviata: al termine controlla la bozza in "Regole" e attivala.',
			};
			this.mostra(avvisi[type] || `${TIPI_JOB[type] || type} avviato.`);
		},
		async annulla(jobId) {
			const res = await this.$store.dispatch('competitor/cancelJob', jobId);
			if (res.error) {
				this.mostra(testoErrore(res.error), 'error');
				return;
			}
			this.segui(res.job);
		},
		// correzione dell'abbinamento: pagina; Indietro e Abbina tornano qui
		apriMatch(prodotto) {
			if (prodotto && prodotto._id) this.$router.push(`/competitor/products/${prodotto._id}/match`);
		},
		async aggiornaJob(jobId) {
			const res = await this.$store.dispatch('competitor/fetchJob', jobId);
			if (res.error || !res.job) return;
			const prima = this.jobs[jobId];
			// copia pagina della taratura arrivata via socket: la segue TaraDialog, qui si ignora
			if (!prima && TIPI_JOB_INTERNI.includes(res.job.type)) return;
			const job = this.normalizza(res.job);
			this.$set(this.jobs, jobId, job);
			if (prima && attivo(prima) && !attivo(job)) {
				const stato = STATI_JOB[job.status] || { testo: job.status };
				const dettaglio = job.status !== 'done' ? testoMessaggio(job) : '';
				this.mostra(`${TIPI_JOB[job.type] || job.type}: ${stato.testo.toLowerCase()}${dettaglio ? ` — ${dettaglio}` : ''}`, job.status === 'done' ? 'success' : 'error');
				this.refreshKey++;
				this.caricaSiti();
			}
		},
		async polling() {
			const inCorso = Object.values(this.jobs).filter(attivo);
			for (const job of inCorso) {
				await this.aggiornaJob(job._id);
			}
		},
	},
};
</script>
